import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatPaymentRates } from '../../utils/helpers';
import SaveButton from './SaveButton';

const SchemeItem = ({ scheme, indented, onRemove }) => (
  <div className={`flex items-center gap-2${indented ? ' ml-6' : ''}`}>
    {indented && (
      <span className="text-gray-300 text-sm select-none" style={{ lineHeight: 1 }}>└</span>
    )}
    <div
      className="flex items-start justify-between px-3 py-1.5 rounded-md flex-1 min-w-0 gap-3"
      style={{ backgroundColor: indented ? '#EEEDDF' : '#F6F5EC' }}
    >
      <div className="min-w-0">
        <span className="text-sm font-semibold break-words" style={{ color: '#222222' }}>
          {scheme.name}
        </span>
        {scheme.fund_collection?.length > 0 && (
          <span className="ml-2 text-xs text-gray-400">
            {scheme.fund_collection.join(', ')}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {scheme.payment_rates?.length > 0 && (
          <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#615DEC' }}>
            {formatPaymentRates(scheme.payment_rates, { maxRates: 2 })}
          </span>
        )}
        <SaveButton funding={scheme} />
      </div>
    </div>
    <button
      onClick={() => onRemove(scheme.id)}
      className="flex-shrink-0 text-gray-300 hover:text-gray-600 transition-colors"
      title={`Remove ${scheme.name} from stack`}
    >
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const CompatibilityModem = ({ stack, compatibleCount, onRemove, onClear }) => {
  const [stackDetail, setStackDetail] = useState(null);
  const [excludedPairings, setExcludedPairings] = useState(new Set());

  const stackKey = (stack || []).map(s => s.id).sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (!stackKey) {
      setStackDetail(null);
      setExcludedPairings(new Set());
      return;
    }
    let cancelled = false;
    api.get(`/funding-schemes/stack-detail/?ids=${stackKey}`)
      .then(r => { if (!cancelled) setStackDetail(r.data); })
      .catch(err => console.error('Failed to fetch stack detail:', err));
    return () => { cancelled = true; };
  }, [stackKey]);

  const handleRemoveSupplementary = (suppId, baseId) => {
    const key = `${suppId}-${baseId}`;
    const newExcluded = new Set(excludedPairings);
    newExcluded.add(key);

    // Check if this supplementary still appears under any other base
    const hasRemainingPairings = stackDetail.groups.some(group =>
      group.base &&
      group.supplementary.some(s => s.id === suppId) &&
      !newExcluded.has(`${suppId}-${group.base.id}`)
    );

    if (hasRemainingPairings) {
      setExcludedPairings(newExcluded);
    } else {
      // No pairings left — clear all exclusions for this scheme and remove from stack
      setExcludedPairings(prev => {
        const cleaned = new Set(prev);
        for (const k of cleaned) {
          if (k.startsWith(`${suppId}-`)) cleaned.delete(k);
        }
        return cleaned;
      });
      onRemove(suppId);
    }
  };

  if (!stack || stack.length === 0) return null;

  // Filter stackDetail against current stack IDs so removed schemes
  // disappear instantly without waiting for the API round-trip
  const stackIds = new Set(stack.map(s => s.id));
  const visibleGroups = (stackDetail?.groups ?? [])
    .map(group => ({
      base: group.base && stackIds.has(group.base.id) ? group.base : null,
      supplementary: group.supplementary.filter(s => stackIds.has(s.id)),
    }))
    .filter(group => group.base || group.supplementary.length > 0);
  const visibleCapitalItems = (stackDetail?.capital_items ?? [])
    .filter(item => stackIds.has(item.id));

  return (
    <div
      className="mb-6 border-2 rounded-lg overflow-hidden"
      style={{
        borderColor: '#615DEC',
        backgroundColor: '#ffffff',
        fontFamily: "'Poppins', system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #E0DFDA' }}>
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#615DEC' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
          <span className="text-sm font-semibold" style={{ color: '#222222' }}>
            {stack.length === 1 ? 'Showing opportunities compatible with:' : 'Showing opportunities compatible with all of:'}
          </span>
        </div>
        <button
          onClick={onClear}
          className="text-xs font-medium hover:underline transition-all"
          style={{ color: '#7C7B7B' }}
        >
          Clear stack
        </button>
      </div>

      {/* Body */}
      {(visibleGroups.length > 0 || visibleCapitalItems.length > 0) && (
        <div className="flex flex-col gap-2 p-4">
          {/* Groups: base + supplementary tree */}
          {visibleGroups.map((group, i) => {
            const visibleSupps = group.base
              ? group.supplementary.filter(s => !excludedPairings.has(`${s.id}-${group.base.id}`))
              : group.supplementary;

            if (!group.base && visibleSupps.length === 0) return null;

            return (
              <div key={group.base?.id ?? `orphan-${i}`} className="flex flex-col gap-1.5">
                {group.base && (
                  <SchemeItem scheme={group.base} onRemove={onRemove} />
                )}
                {visibleSupps.map(supp => (
                  <SchemeItem
                    key={supp.id}
                    scheme={supp}
                    indented={!!group.base}
                    onRemove={group.base
                      ? () => handleRemoveSupplementary(supp.id, group.base.id)
                      : () => onRemove(supp.id)
                    }
                  />
                ))}
              </div>
            );
          })}

          {/* Capital items */}
          {visibleCapitalItems.length > 0 && (
            <>
              <div className="flex items-center gap-2 mt-2 mb-1">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Capital Items</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              {visibleCapitalItems.map(item => (
                <SchemeItem key={item.id} scheme={item} onRemove={onRemove} />
              ))}
            </>
          )}
        </div>
      )}

      {/* Compatible count */}
      {compatibleCount != null && (
        <div className="flex justify-end px-4 py-2" style={{ borderTop: '1px solid #E0DFDA' }}>
          <span className="text-xs font-medium" style={{ color: '#615DEC' }}>
            {compatibleCount} compatible {compatibleCount === 1 ? 'opportunity' : 'opportunities'}
          </span>
        </div>
      )}
    </div>
  );
};

export default CompatibilityModem;
